
import { defineStore } from "pinia";
import { ref } from "vue";
import planService from "@/services/super-admin/planService";
import { toast } from "@/utils/toast";

export const usePlanStore = defineStore("plan", () => {
  const plans = ref([]);
  const plan = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const listParams = ref({ per_page: 1000 });

  async function fetchPlans(params) {
    loading.value = true;
    error.value = null;
    try {
      if (params !== undefined) {
        listParams.value = { per_page: 1000, ...params };
      }

      const res = await planService.getAll(listParams.value);
      plans.value = res.data?.data || res.data || [];
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch plans";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchPlan(id) {
    loading.value = true;
    error.value = null;
    try {
      const res = await planService.get(id);
      plan.value = res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to fetch plan";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function createPlan(data) {
    loading.value = true;
    error.value = null;
    try {
      const res = await planService.create(data);
      toast("Plan created successfully");
      await fetchPlans();
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to create plan";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updatePlan(id, data) {
    loading.value = true;
    error.value = null;
    try {
      const res = await planService.update(id, data);
      toast("Plan updated successfully");
      await fetchPlans();
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to update plan";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deletePlan(id) {
    loading.value = true;
    error.value = null;
    try {
      await planService.delete(id);
      toast("Plan deleted successfully");
      await fetchPlans();
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to delete plan";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function syncAnnualLicensePlan(id) {
    loading.value = true;
    error.value = null;
    try {
      const res = await planService.syncAnnualLicense(id);
      toast("Annual license settings synced successfully");
      await fetchPlans();
      return res.data;
    } catch (err) {
      error.value = err.response?.data?.message || "Failed to sync annual license settings";
      toast(error.value, "error");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    plans,
    plan,
    loading,
    error,
    fetchPlans,
    fetchPlan,
    createPlan,
    updatePlan,
    deletePlan,
    syncAnnualLicensePlan,
  };
});
